import { useState, useEffect } from 'react';
import api from '../services/api';

const WINDOWS = [30, 90, 180];

const STATUS_STYLE = {
  failed: 'bg-red-50 text-red-700',
  refunded: 'bg-blue-50 text-blue-700',
  abandoned: 'bg-gray-100 text-gray-600',
};

// business = our integration bug → loud. customer = their action. gateway/
// razorpay = transient/external. unknown = pre-categorization or no source.
const CATEGORY_STYLE = {
  business: 'bg-red-100 text-red-800',
  customer: 'bg-amber-50 text-amber-700',
  gateway: 'bg-blue-50 text-blue-700',
  razorpay: 'bg-purple-50 text-purple-700',
  unknown: 'bg-gray-100 text-gray-500',
};

function fmtDate(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('en-IN', {
    day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit',
  });
}

function rupees(paise) {
  if (paise == null) return '—';
  return `₹${(paise / 100).toLocaleString('en-IN')}`;
}

export default function PaymentsPage() {
  const [data, setData] = useState(null);
  const [days, setDays] = useState(90);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    async function fetchData() {
      try {
        setLoading(true);
        setError(null);
        const result = await api.getPaymentFailures(days);
        if (!cancelled) setData(result.data);
      } catch (err) {
        if (!cancelled) setError(err.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    fetchData();
    return () => { cancelled = true; };
  }, [days]);

  if (error) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
          Error loading payments: {error}
        </div>
      </div>
    );
  }

  const summary = data?.summary || {};
  const items = data?.items || [];
  const cat = summary.by_category || {};

  return (
    <div className="p-6">
      {/* Header + window selector */}
      <div className="mb-6 flex items-start justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-2xl font-bold text-teacher-text-primary">Payments — Failures & Refunds</h1>
          <p className="text-teacher-text-secondary">
            Non-completed payments and refunds, with the Razorpay error reason and category.
            Source: <code className="text-xs bg-teacher-bg-secondary px-1 rounded">payments</code>
          </p>
        </div>
        <div className="flex gap-1 bg-teacher-bg-secondary rounded-lg p-1">
          {WINDOWS.map((w) => (
            <button
              key={w}
              onClick={() => setDays(w)}
              className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                days === w
                  ? 'bg-white text-teacher-text-primary shadow-sm'
                  : 'text-teacher-text-secondary hover:text-teacher-text-primary'
              }`}
            >
              {w}d
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="h-48 flex items-center justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
        </div>
      ) : (
        <>
          {/* Summary cards */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            <SummaryCard label="Failed" value={summary.failed || 0} sub={`last ${days} days`} accent="border-l-red-500" />
            <SummaryCard label="Refunded" value={summary.refunded || 0} sub="money returned" accent="border-l-blue-500" />
            <SummaryCard label="Abandoned" value={summary.abandoned || 0} sub="started, never paid" accent="border-l-gray-400" />
            <SummaryCard label="Recurring failed" value={summary.recurring_failed || 0} sub="auto-renewal debits" accent="border-l-amber-500" />
          </div>

          {/* Failure category breakdown */}
          <div className="bg-white rounded-xl p-6 shadow-sm border border-teacher-border mb-6">
            <h3 className="text-sm font-medium text-teacher-text-secondary mb-3">
              Failures by source <span className="text-teacher-text-tertiary">(who needs to act)</span>
            </h3>
            <div className="flex flex-wrap gap-2">
              <CategoryChip label="Business (our bug)" count={cat.business || 0} kind="business" />
              <CategoryChip label="Customer" count={cat.customer || 0} kind="customer" />
              <CategoryChip label="Gateway" count={cat.gateway || 0} kind="gateway" />
              <CategoryChip label="Razorpay" count={cat.razorpay || 0} kind="razorpay" />
              <CategoryChip label="Unknown" count={cat.unknown || 0} kind="unknown" />
            </div>
            {(cat.business || 0) > 0 && (
              <p className="text-xs text-red-600 mt-3 font-medium">
                ⚠ {cat.business} business-source failure(s) — these are our integration bugs, not the customer.
              </p>
            )}
            <p className="text-xs text-teacher-text-tertiary mt-2">
              "Unknown" = failures recorded before structured categorization, or with no source.
            </p>
          </div>

          {/* Items table */}
          <div className="bg-white rounded-xl p-6 shadow-sm border border-teacher-border">
            <h3 className="text-lg font-semibold text-teacher-text-primary mb-4">
              Transactions ({items.length})
            </h3>
            {items.length === 0 ? (
              <p className="text-teacher-text-tertiary text-center py-8">
                No failed, refunded, or abandoned payments in the last {days} days.
              </p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-teacher-border">
                      {['When', 'Status', 'User', 'Plan', 'Amount', 'Method', 'Category', 'Reason / Detail'].map((h) => (
                        <th key={h} className="text-left py-2 px-3 text-xs font-semibold text-teacher-text-secondary uppercase">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((it) => (
                      <tr key={it.order_id} className="border-b border-gray-50 align-top">
                        <td className="py-2 px-3 text-teacher-text-secondary whitespace-nowrap">{fmtDate(it.created_at)}</td>
                        <td className="py-2 px-3">
                          <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${STATUS_STYLE[it.status] || 'bg-gray-100 text-gray-600'}`}>
                            {it.status}
                          </span>
                          {it.is_recurring && (
                            <span className="ml-1 inline-block px-1.5 py-0.5 rounded text-[10px] bg-amber-50 text-amber-700">renewal</span>
                          )}
                        </td>
                        <td className="py-2 px-3 text-teacher-text-primary whitespace-nowrap">
                          <div>{it.name || '—'}</div>
                          <div className="text-xs text-teacher-text-tertiary">{it.phone || it.user_id || ''}</div>
                        </td>
                        <td className="py-2 px-3 text-teacher-text-secondary whitespace-nowrap">
                          {it.tier_id || '—'}{it.billing_period ? ` · ${it.billing_period}` : ''}
                        </td>
                        <td className="py-2 px-3 text-teacher-text-primary whitespace-nowrap">{rupees(it.amount_paise)}</td>
                        <td className="py-2 px-3 text-teacher-text-secondary">{it.payment_method || '—'}</td>
                        <td className="py-2 px-3">
                          {it.failure_category ? (
                            <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${CATEGORY_STYLE[it.failure_category] || CATEGORY_STYLE.unknown}`}>
                              {it.failure_category}
                            </span>
                          ) : (
                            <span className="text-teacher-text-tertiary">—</span>
                          )}
                        </td>
                        <td className="py-2 px-3 text-teacher-text-secondary max-w-md">
                          {it.status === 'refunded'
                            ? <span className="text-blue-700">{it.refund_id || 'refunded'}</span>
                            : (it.error_reason
                                ? <span><code className="text-xs bg-teacher-bg-secondary px-1 rounded">{it.error_reason}</code>{it.error_description ? ` — ${it.error_description}` : ''}</span>
                                : (it.error_description || '—'))}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}

function SummaryCard({ label, value, sub, accent }) {
  return (
    <div className={`rounded-xl p-6 shadow-sm border border-teacher-border border-l-4 ${accent} bg-white`}>
      <h3 className="text-sm font-medium text-teacher-text-secondary mb-1">{label}</h3>
      <span className="text-3xl font-bold text-teacher-text-primary">{value}</span>
      <p className="text-xs text-teacher-text-tertiary mt-1">{sub}</p>
    </div>
  );
}

function CategoryChip({ label, count, kind }) {
  return (
    <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium ${CATEGORY_STYLE[kind]}`}>
      {label}
      <span className="font-bold">{count}</span>
    </span>
  );
}
